import axios from 'axios'
import useLocalStorage from './useLocalStorage'

import type { Partner, PartnerParameter, PartnerUserAuth } from '../types/types'

export default function useIntegration() {
  const { t } = useI18n()
  const { user, stores } = useLocalStorage()
  const hasAnActiveIntegration = ref(false)

  const partnersParameters = ref<PartnerParameter[]>()

  const getVisiblePartners = async () => {
    const { data } = await axios.get('partners', { params: { isVisibleInMarketplace: 1 } })
    return data.data
  }

  const getPartnersParameters = async () => {
    const { data } = await axios.get('partner-parameters', {
      params: {
        merchantId: user.value.merchant.id
      }
    })
    partnersParameters.value = data.data
    hasAnActiveIntegration.value = data.data.some((p) => p.isActive)
    return data.data
  }

  const checkHasActiveIntegration = async () => {
    const partnerParameters = await getPartnersParameters()
    hasAnActiveIntegration.value = partnerParameters.some((p) => p.isActive)
    return hasAnActiveIntegration.value
  }

  const howToUrl = (slug: string | undefined) => {
    if (!slug) console.warn('Slug is undefined. Cannot generate how-to URL.')

    switch (slug) {
      case 'zelty':
        return 'https://heypongohelp.zendesk.com/hc/fr/sections/21745885618065-Pongo-Fid%C3%A9lit%C3%A9-x-Zelty-POS'
      case 'tillersystems':
        return 'https://heypongohelp.zendesk.com/hc/fr/sections/21745883060113-Pongo-Fid%C3%A9lit%C3%A9-x-SumUp'
      case 'lightspeed':
        return 'https://heypongohelp.zendesk.com/hc/fr/sections/21745939525137-Pongo-Fid%C3%A9lit%C3%A9-x-Lightspeed'
      case 'shopify':
        return 'https://heypongohelp.zendesk.com/hc/fr/sections/21746021911697-Pongo-X-Shopify-Web'
      case 'woocommerce':
        return 'https://heypongohelp.zendesk.com/hc/fr/sections/21746007498257-Pongo-X-WooCommerce'
      case 'menlog':
        return 'https://heypongohelp.zendesk.com/hc/fr/sections/21745880172561-Pongo-Fid%C3%A9lit%C3%A9-x-Menlog'
      case 'tabesto':
        return 'https://heypongohelp.zendesk.com/hc/fr/sections/21745959918993-Pongo-Fid%C3%A9lit%C3%A9-x-Tabesto'
      case 'izipass':
        return 'https://heypongohelp.zendesk.com/hc/fr/categories/21745273657233-Int%C3%A9grations'
      case 'carre_pos':
        return 'https://heypongohelp.zendesk.com/hc/fr/sections/21745924029969-Pongo-x-Carr%C3%A9POS'
      case 'laddition':
        return 'https://heypongohelp.zendesk.com/hc/fr/sections/21745913035921-Pongo-Fid%C3%A9lit%C3%A9-x-L-Addition'
      default:
        return 'https://heypongohelp.zendesk.com/hc/fr/categories/21745273657233-Int%C3%A9grations'
    }
  }

  const statusUrl = (partnerId: number | undefined, merchantId: number | undefined) => {
    if (partnerId && merchantId) return `https://status.heypongo.com?merchantId=${merchantId}&partnerId=${partnerId}`

    console.warn('Partner ID or Merchant ID is undefined. Cannot generate status URL.')

    return ''
  }

  const displayPartnerName = (name: string, slug: string) => {
    if (name === 'laddition' || name === 'tillersystems') {
      return t(`integrations.${slug}.name`)
    }
    return name
  }

  const availableOnboardingIntegrations = [
    {
      slug: 'tillersystems',
      logo: `${import.meta.env.VITE_AWS_STORAGE_URL}/loyalty/logo_sumup.png`
    },
    {
      slug: 'shopify',
      logo: `${import.meta.env.VITE_AWS_STORAGE_URL}/loyalty/logo_shopify.png`
    },
    {
      slug: 'zelty',
      logo: `${import.meta.env.VITE_AWS_STORAGE_URL}/loyalty/logo_zelty.png`
    },
    {
      slug: 'laddition',
      logo: `${import.meta.env.VITE_AWS_STORAGE_URL}/loyalty/logo_laddition.png`
    },
    {
      slug: 'lightspeed',
      logo: `${import.meta.env.VITE_AWS_STORAGE_URL}/loyalty/logo_lightspeed.png`
    },
    {
      slug: 'menlog',
      logo: `${import.meta.env.VITE_AWS_STORAGE_URL}/loyalty/logo_menlog.png`,
      isActive: true
    }
  ]

  const getPartnerDefaultSettings = async (partnerId: number) => {
    const { data } = await axios.get(`partner-defaults/${partnerId}`)
    const inputToEdit = ['pointsClaimingTitle', 'pointsClaimingSubtitle', 'failedTitle', 'failedSubtitle']
    const defaultValues = data.data
    const isFormal = defaultValues.isFormal
    for (const inputElement of inputToEdit) {
      defaultValues[inputElement] = isFormal ? defaultValues[inputElement].formal : defaultValues[inputElement].classic
    }

    return defaultValues
  }

  const createDefaultPartner = async (partner: Partner, isActive: boolean, finalRedirectUri?: string) => {
    const defaultParameters = await getPartnerDefaultSettings(partner.id)
    const { data } = await axios.post(`/partner-parameters`, {
      ...defaultParameters,
      isProductPriceSyncEnabled: false,
      isProductSyncEnabled: false,
      merchantId: user.value.merchant.id,
      partnerId: partner.id,
      storeIds: [...stores.value.map((s) => s.id)],
      isActive: isActive,
      finalRedirectUri: finalRedirectUri,
      apiUrl: partner?.apiUrl
    })
    return data.data
  }

  const updatePartnerParameters = async (partner: Partner) => {
    const params = {
      ...partner,
      storeIds: partner.stores.map((partnerStore) => partnerStore.id)
    }
    const { data } = await axios.put(`/partner-parameters/${partner.id}`, {
      ...params
    })
    return data.data
  }

  const loginToPartner = async (ids: PartnerUserAuth, partnerSlug: string) => {
    const { data } = await axios.post(
      '/partner/auth',
      {
        auth: {
          login: ids.login,
          password: ids.password
        },
        merchantId: user.value.merchant.id
      },
      {
        params: {
          partner: partnerSlug
        }
      }
    )
    return data.data
  }

  return {
    displayPartnerName,
    availableOnboardingIntegrations,
    checkHasActiveIntegration,
    getVisiblePartners,
    getPartnersParameters,
    createDefaultPartner,
    updatePartnerParameters,
    loginToPartner,
    howToUrl,
    statusUrl
  }
}
